import React, { useEffect, useMemo, useState } from "react";
import AdminHeading from "../Heading/AdminHeading";
import ListTable from "Components/CommonComponents/ListTable";
import { ClipLoader } from "react-spinners";
import ErrorModal from "Components/Other Functions/popup";
import AlertMessage from "Components/Other Functions/AlertMessage";
import { useSelector } from "react-redux";
import GetApiService from "Components/Api/getapiService";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { a188, a730 } from "Components/Api/RootApiPath";
import { Add } from "@mui/icons-material";
import CustomerTable from "Components/support/customervendor/customer";
import { Icon } from "@iconify/react";
import { invoiceListColumns, purchaseListColumns } from "utils/helpers";
import {
  fetchInvoice,
  fetchInvoiceById,
  fetchInvoiceList,
} from "services/invoiceServices";
import PageLoader from "Components/PageLoader";
import { generateBillPDF } from "Components/Other Functions/GenerateBillPDF";
import useScreenSize from "hooks/useScreenSize";
import AdminBreadCrump from "../Heading/AdminBreadCrump";
import { createEinvoiceAndWayBill } from "services/verification";
import { verifyGST, getStateCodeFromGST } from "services/gstService";
import QRCode from "qrcode";
import toast from "react-hot-toast";
import Modal from "react-modal"; // Import Modal for popup
import exportToExcel from "Components/CommonComponents/ExcelDownload";

const AdminPurchaseEntriesList = () => {
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);
  const [showFailureModal, setShowFailureModal] = useState(false);
  const [failedProduct, setFailedProduct] = useState(null);
  const allStates = useSelector((state) => state);
  const navigate = useNavigate();
  const adminLoggedIn = allStates.reducer1;
  const clientCode = adminLoggedIn.ClientCode;
  const apiService = new GetApiService(clientCode);
  const InvoiceFormat = parseInt(adminLoggedIn?.Clients?.InvoiceFormat);
  const isSmallScreen = useScreenSize();
  const [searchInput, setSearchInput] = useState("");

  const {
    data: InvoiceList,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["invoice/getInvoiceList", clientCode],
    queryFn: fetchInvoiceList,
    refetchOnWindowFocus: false,
    // cacheTime: 1000 * 60 * 2,
    // staleTime: 1000 * 60 * 2,
  });
  const {
    data: Invoice,
    isError: InvoiceIsError,
    isLoading: InvoiceIsLoading,
    error: InvoiceError,
    mutate,
  } = useMutation({ mutationFn: fetchInvoice });

  const invoiceList = useMemo(() => {
    const formattedData = InvoiceList?.length
      ? InvoiceList.map((item, index) => ({
          // serialNumber: index + 1,  // If you need a sequential ID
          ...item,
          CustomerId: item?.Customer?.FirstName,
          GrossWt: (+(item?.GrossWt || 0))?.toFixed(3),
          NetWt: (+(item?.NetWt || 0))?.toFixed(3),
        }))
      : [];
    return formattedData;
  }, [InvoiceList]);

  const handleEditData = (product) => {
    navigate(`/edit_invoice_new/${product?.Id}`);
  };
  const columns = [
    { key: "Id", label: "Id" },
    { key: "InvoiceNo", label: "Invoice Number" },
    { key: "InvoiceDate", label: "Invoice Date" },
    { key: "InvoiceStatus", label: "Invoice Status" },
    { key: "FirstName", label: "Name" },
    { key: "CustomerId", label: "Customer Id" },
  ];

  useEffect(() => {
    if (Invoice) {
      generateBillPDF(
        Invoice?.InvoiceItem,
        Invoice?.Customer,
        InvoiceFormat,
        Invoice,
        adminLoggedIn,
        isSmallScreen
      );
    }
  }, [Invoice]);

  const estimateDistance = (fromPin, toPin) => {
    if (!fromPin || !toPin) return 150;
    return Math.min(Math.abs(fromPin - toPin), 200);
  };

  const createEinvoceEwayBill = async (product) => {
    const formatDateToDDMMYYYY = (date) => {
      const d = new Date(date);
      const day = String(d.getDate()).padStart(2, "0");
      const month = String(d.getMonth() + 1).padStart(2, "0");
      const year = d.getFullYear();
      return `${day}/${month}/${year}`;
    };

    try {
      // Pre-calculate values for the payload
      const calculatedItems = product.InvoiceItem?.map((item, index) => {
        const quantity = isNaN(parseFloat(item.Quantity))
          ? 0
          : parseFloat(item.Quantity);
        const unitPrice = parseFloat(item.Price) || 0;
        const discount = parseFloat(item.Discount) || 0;
        const gstRate = parseFloat(item.GSTAmount) || 0;

        const totAmt = quantity * unitPrice; // Total Amount
        const assAmt = totAmt - discount; // Assessable Amount
        const igstAmt = (assAmt * gstRate) / 100; // IGST Amount
        const cgstAmt = 0; // CGST Amount (if applicable)
        const sgstAmt = 0; // SGST Amount (if applicable)
        const cessAmt = parseFloat(item.CESSAmount || 0); // CESS Amount (if applicable)

        // Ensure valid HSN code (fallback to a default valid code if invalid)
        const hsnCode =
          item.HSNCode &&
          item.HSNCode !== "0" &&
          /^[0-9]{4,8}$/.test(item.HSNCode)
            ? item.HSNCode.toString().padStart(4, "0").slice(0, 8)
            : "1001"; // Default valid HSN code

        // Ensure valid Unit Quantity Code (fallback to "PCS" if invalid)
        const validUnits = ["PCS", "KG", "LTR", "MTR"]; // Add more valid units as needed
        const unit = validUnits.includes(item.CategoryName)
          ? item.CategoryName
          : "PCS";

        // Calculate Total Item Value
        const totItemVal = assAmt + igstAmt + cgstAmt + sgstAmt + cessAmt;

        return {
          SlNo: (index + 1).toString(),
          PrdDesc: item.ProductName || "Default Product",
          IsServc: "N",
          HsnCd: hsnCode,
          Barcde: (item.Barcode || "123456").toString(),
          Qty: quantity.toFixed(2),
          FreeQty: parseFloat(item.Qty || 10).toFixed(2),
          Unit: unit,
          UnitPrice: unitPrice.toFixed(2),
          TotAmt: totAmt.toFixed(2),
          Discount: discount.toFixed(2),
          PreTaxVal: "0.00", // Pre-Tax Value (if applicable)
          AssAmt: assAmt.toFixed(2),
          GstRt: gstRate.toFixed(2),
          IgstAmt: igstAmt.toFixed(2),
          CgstAmt: cgstAmt.toFixed(2),
          SgstAmt: sgstAmt.toFixed(2),
          CesRt: item.CESSRate || 5,
          CesAmt: cessAmt.toFixed(2),
          CesNonAdvlAmt: parseFloat(item.CESSNonAdvalAmount || 0).toFixed(2),
          StateCesRt: item.StateCESSRate || 12,
          StateCesAmt: parseFloat(item.StateCESSAmount || 0).toFixed(2),
          StateCesNonAdvlAmt: parseFloat(
            item.StateCESSNonAdvalAmount || 0
          ).toFixed(2),
          OthChrg: parseFloat(item.OtherCharges || 0).toFixed(2),
          TotItemVal: totItemVal.toFixed(2), // Correctly calculated Total Item Value
          OrdLineRef: item.OrderLineReference || "3256",
          OrgCntry: item.OriginCountry || "IN",
          PrdSlNo: item.InvocieItemId?.toString() || (index + 1).toString(),
          AttribDtls: [
            {
              Nm: item.CategoryName || "PCS",
              Val: quantity.toFixed(2),
            },
          ],
        };
      });

      // Calculate overall values for ValDtls
      const totalAssVal = calculatedItems.reduce(
        (sum, item) => sum + parseFloat(item.AssAmt),
        0
      );
      const totalIgstVal = calculatedItems.reduce(
        (sum, item) => sum + parseFloat(item.IgstAmt),
        0
      );
      const totalCgstVal = calculatedItems.reduce(
        (sum, item) => sum + parseFloat(item.CgstAmt),
        0
      );
      const totalSgstVal = calculatedItems.reduce(
        (sum, item) => sum + parseFloat(item.SgstAmt),
        0
      );
      const totalDiscount = calculatedItems.reduce(
        (sum, item) => sum + parseFloat(item.Discount),
        0
      );
      const totalOtherCharges = calculatedItems.reduce(
        (sum, item) => sum + parseFloat(item.OthChrg),
        0
      );

      const totalInvoiceValue =
        totalAssVal +
        totalIgstVal +
        totalCgstVal +
        totalSgstVal +
        totalOtherCharges;

      const sellerPin = parseInt(
        "560001" || adminLoggedIn.Clients?.PostalCode,
        10
      );
      const buyerPin = parseInt(
        "431001" || product.Customer?.CurrAddPincode,
        10
      );

      const distance = estimateDistance(sellerPin, buyerPin);
      let transporterName = "";
      let transporterDoc = "TRANS/DOC/0001";

      try {
        const gstResponse = await verifyGST(
          "27AAACI9260R002" || adminLoggedIn?.Clients.GSTNo
        );
        console.log("GST Response:", gstResponse);
        transporterName = gstResponse?.tradeNam || transporterName;
        transporterDoc = gstResponse?.lgnm || transporterDoc;
      } catch (error) {
        console.warn(
          "Could not fetch transporter info, using fallback values."
        );
      }

      // Prepare payload for API call
      const payload = {
        Version: "1.1", // Add the required Version field
        TranDtls: {
          TaxSch: "GST", // Tax Scheme
          SupTyp: "B2B", // Supply Type
        },
        DocDtls: {
          Typ: "INV", // Document Type
          No: `INV${Date.now()}`, // for dummmy we use --> No: `INV${Date.now()}`,
          Dt: formatDateToDDMMYYYY(product.InvoiceDate || new Date()), // Document Date
        },
        SellerDtls: {
          Gstin: "27AAACI9260R002" || adminLoggedIn?.Clients.GSTNo, // GST Identification Number
          LglNm: adminLoggedIn?.Clients.UserName, // Legal Name
          TrdNm: adminLoggedIn?.Clients.UserName, // Trade Name
          Addr1: `${adminLoggedIn.Clients.City},${adminLoggedIn?.Clients?.StreetAddress},`, // Address Line 1
          Addr2: `${adminLoggedIn.Clients.Town},${adminLoggedIn.Clients.PostalCode},${adminLoggedIn.Clients?.Country}`, // Address Line 2
          Loc: `${adminLoggedIn.Clients.State}` || "GANDHINAGAR", // Location
          Pin: parseInt(adminLoggedIn.Clients.PostalCode, 10) || 560001, // Ensure Pin is an integer
          Stcd:
            getStateCodeFromGST(
              "27AAACI9260R002" || adminLoggedIn.Clients?.GSTNo
            ) || "27", // State Code
          Ph: adminLoggedIn.Clients.Mobile, // Phone
          Em: adminLoggedIn.Clients.EmailForOTP, // Email
        },
        BuyerDtls: {
          Gstin: "27AABCK6637R2ZS" || product?.Customer?.GstNo,
          LglNm: product.Customer?.FirstName || "Irisonyx_mh",
          TrdNm: product.Customer?.FirstName || "SUNIL MULE",
          Pos: product.Customer?.DeliveryAddress || "12",
          Addr1: `${product.Customer?.CurrAddTown || ""},${
            product.Customer?.CurrAddStreet || ""
          }`,
          Addr2: `${product.Customer?.City || "null"},${
            product.Customer?.CurrAddState || "Maharashtra"
          }`,
          Loc: product.Customer?.CurrAddStreet || "GANDHINAGAR",
          Pin: parseInt(product.Customer?.CurrAddPincode, 10) || 431001,
          Stcd:
            getStateCodeFromGST(
              "27AABCK6637R2ZS" || product?.Customer?.GstNo
            ) || "27",
          Ph: product.Customer?.Mobile || "9834289526",
          Em: product?.Email || null,
        },
        ItemList: calculatedItems,
        ValDtls: {
          AssVal: totalAssVal.toFixed(2),
          CgstVal: totalCgstVal.toFixed(2),
          SgstVal: totalSgstVal.toFixed(2),
          IgstVal: totalIgstVal.toFixed(2),
          CesVal: "0.00",
          StCesVal: "0.00",
          Discount: totalDiscount.toFixed(2),
          OthChrg: totalOtherCharges.toFixed(2),
          RndOffAmt: "0.00",
          TotInvVal: totalInvoiceValue.toFixed(2),
          TotInvValFc: totalInvoiceValue.toFixed(2),
        },
        EwayApiData: {
          Distance: distance,
          TransMode: "1",
          TransId: "12AWGPV7107B1Z1" || adminLoggedIn?.Clients.GSTNo,
          TransName: "Dummy Logistics Pvt Ltd" || transporterName,
          TransDocNo: "TRANS/DOC/0001" || transporterDoc,
          TransDocDt: formatDateToDDMMYYYY(product.InvoiceDate || new Date()),
          VehNo: "KA12ER1234",
          VehType: "R",
          ExpShipDtls: {
            Addr1: `${adminLoggedIn.Clients.City},${adminLoggedIn?.Clients?.StreetAddress},`, // Address Line 1
            Addr2: `${adminLoggedIn.Clients.Town},${adminLoggedIn.Clients.PostalCode},${adminLoggedIn.Clients?.Country}`,
            Loc: `${adminLoggedIn.Clients.State}` || "GANDHINAGAR",
            Pin: parseInt(adminLoggedIn.Clients.PostalCode, 10) || 560001,
            Stcd:
              getStateCodeFromGST(
                adminLoggedIn.Clients?.GSTNo || "27AAACI9260R002"
              ) || "27",
          },
          DispDtls: {
            Nm: product.Customer?.FirstName || "Customer",
            Addr1: `${product.Customer?.CurrAddTown || ""},${
              product.Customer?.CurrAddStreet || ""
            }`,
            Addr2: `${product.Customer?.City || "null"},${
              product.Customer?.CurrAddState || "Maharashtra"
            }`,
            Loc: product.Customer?.CurrAddStreet || "GANDHINAGAR",
            Pin: parseInt(product.Customer?.CurrAddPincode, 10) || 431001,
            Stcd:
              getStateCodeFromGST(
                "27AABCK6637R2ZS" || product?.Customer?.GstNo
              ) || "27",
          },
        },
      };

      console.log("adminLoggedIn : --> ", adminLoggedIn);

      console.log(`Payload --->`, payload);

      const credentials = {
        clientId: "IRIS3a3621512a4af7b2b0cb390ca6a4877a", //adminLoggedIn?.Clients?.ClientCode,
        clientSecret: "d6c6902af5a18966075c4562386d9a2c", //adminLoggedIn?.Clients?.ClientCode,
        userName: "Irisonyx_mh", //adminLoggedIn?.Clients?.UserName,
        password: "Irisonyxmh_01", //adminLoggedIn?.Clients?.Password,
        gstin: "27AAACI9260R002", //adminLoggedIn?.Clients?.GSTNo,
      };

      const results = await createEinvoiceAndWayBill(payload, credentials);

      // if (
      //   results.Status === 0 &&
      //   results.ErrorDetails?.[0]?.ErrorCode === "2150"
      // ) {
      //   // Handle Duplicate IRN error
      //   const duplicateInfo = results.InfoDtls?.[0]?.Desc;
      //   if (duplicateInfo) {
      //     console.warn("Duplicate IRN detected. Using existing IRN details.");
      //     // Generate invoice using the existing IRN

      //     return {
      //       IRN: duplicateInfo.Irn,
      //       AckNo: duplicateInfo.AckNo,
      //       AckDt: duplicateInfo.AckDt,
      //       EInvoiceNumber: einvoiceData?.InvoiceNumber || "N/A",
      //       EWayBillNumber: "N/A",
      //       QRCode: null,
      //     };
      //   }
      // }

      const irn = results.IRN?.Irn || "N/A";
      const einvoiceNumber = results.IRN?.SignedInvoice || "N/A";
      const ewayBillNumber = results.EWayBill?.EwbNo || "N/A";
      const ewayBillDate = results.EWayBill?.EwbDt || "N/A";
      const ewayBillValidTill = results.EWayBill?.EwbValidTill || "N/A";
      const IrnQrCode = results.IRN?.SignedQRCode || "N/A";
      const qrCode = await QRCode.toDataURL(IrnQrCode);

      // console.log("EWayBill Details:", {
      //   EWayBillNumber: ewayBillNumber,
      //   EWayBillDate: ewayBillDate,
      //   EWayBillValidTill: ewayBillValidTill,
      // });

      // Generate QR Code
      // const qrCodeData = `IRN: ${irn}\nE-Invoice: ${einvoiceNumber}\nE-Way Bill: ${ewayBillNumber}\nEWB Date: ${ewayBillDate}\nValid Till: ${ewayBillValidTill}`;
      // const qrCode = await QRCode.toDataURL(qrCodeData);

      const einvoiceAndWayBill = {
        IRN: irn,
        EInvoiceNumber: einvoiceNumber,
        EWayBillNumber: ewayBillNumber,
        EWayBillDate: ewayBillDate,
        EWayBillValidTill: ewayBillValidTill,
        QRCode: qrCode,
      };

      // setLoading(true);

      // setTimeout(() => {
      //   setLoading(false);
      //   generateBillPDF(
      //     product.InvoiceItem,
      //     product.Customer,
      //     InvoiceFormat,
      //     einvoiceAndWayBill,
      //     adminLoggedIn,
      //     isSmallScreen
      //   );
      //   toast.success("PDF generated successfully!");
      //   setInvoiceIsLoading(false);
      // }, 2000);

      return einvoiceAndWayBill;
    } catch (error) {
      console.error(
        "Error generating E-Invoice and E-Way Bill:",
        error.message
      );
      toast.error(
        "Failed to generate E-Invoice and E-Way Bill. Please try again later."
      );
    }
  };

  const handlePrint = async (product) => {
    console.log(product);
    // generateBillPDF(
    //   product.InvoiceItem,
    //   product.Customer,
    //   InvoiceFormat,
    //   product,
    //   adminLoggedIn,
    //   isSmallScreen
    // );

    try {
      setLoading(true);
      const invoiceEntry = await fetchInvoiceById({
        queryKey: [, product?.Id, clientCode],
      });
      // const einvoiceAndWayBill = await createEinvoceEwayBill(product);

      generateBillPDF(
        invoiceEntry?.InvoiceItem,
        invoiceEntry?.Customer,
        InvoiceFormat,
        invoiceEntry,
        // einvoiceAndWayBill,
        adminLoggedIn,
        isSmallScreen
      );
      //  const invoiceEntry = await fetch(a730, {
      //         method: "POST",
      //         headers: {
      //           "Content-Type": "application/json",
      //         },
      //         body: JSON.stringify({ id:product?.Id, clientCode }),
      //       });

      //       console.log(invoiceEntry)

      // Ensure IRN and E-Way Bill are generated successfully
      // if (!einvoiceAndWayBill) {
      //   throw new Error("Failed to generate IRN or E-Way Bill");
      // } else {
      //   console.log(`enter --- for pdf =--`)
      //   generateBillPDF(
      //     invoiceEntry?.InvoiceItem,
      //     invoiceEntry?.Customer,
      //     InvoiceFormat,
      //     invoiceEntry,
      //     adminLoggedIn,
      //     isSmallScreen
      //   );

      //   toast.success("PDF generated successfully!");
      // }
    } catch (error) {
      console.error("Error generating E-Invoice and E-Way Bill:", error);
      console.error(
        "Error generating E-Invoice and E-Way Bill:",
        error.message
      );

      toast.error("Unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateWithoutIRN = () => {
    if (failedProduct) {
      try {
        // Generate the PDF without IRN and other details
        generateBillPDF(
          failedProduct.InvoiceItem,
          failedProduct.Customer,
          InvoiceFormat,
          null, // Pass null for IRN and E-Way Bill details
          adminLoggedIn,
          isSmallScreen
        );
        toast.success("PDF generated successfully without IRN and E-Way Bill!");
      } catch (error) {
        console.error("Error generating PDF without IRN:", error);
        toast.error("Failed to generate PDF. Please try again.");
      }
    } else {
      console.error("No product data available for PDF generation.");
      toast.error("Failed to generate PDF. No product data available.");
    }
    setShowFailureModal(false); // Close the modal
  };

  const DownloadExcel = (data) => {
    const headers = {
      "S.No": null, // Auto-generated
      "Invoice No": "InvoiceNo",
      "Customer Name": "CustomerId",
      "Gross Wt": "GrossWt",
      "Net Wt": "NetWt",
      "F+W Wt": "TotalFineMetal",
      "Invoice Amount": "TotalNetAmount",
      "GST Amount": "TotalGSTAmount",
      "Net Amount": "TotalPurchaseAmount",
      "Paid Amount": "ReceivedAmount",
      "Paid Metal": "PaidMetal",
      "Balance Amount": "BalanceAmount",
    };
    exportToExcel(data, headers, "InvoiceList.xlsx");
  };
  return (
    <>
      <div>
        <AdminHeading />
        {isLoading || (InvoiceIsLoading && <PageLoader />)}
        {loading ? (
          // Show spinner while loading
          <div className="spinner-container">
            <ClipLoader size={50} color={"#123abc"} loading={loading} />
            <p>Loading data...</p>
          </div>
        ) : (
          <>
            <div>
              <ErrorModal
                isOpen={showModal}
                onRequestClose={() => {
                  setShowModal(false); // Close the modal
                  navigate("/adminhome"); // Redirect to /adminhome
                }}
                onReload={() => {}} // Pass reload function
                message={errorMessage}
              />

              <div className="adminMainBodyBox">
                {showError ? (
                  <AlertMessage message={messageToShow} type={messageType} />
                ) : null}
              </div>
            </div>
          </>
        )}
        {isSmallScreen ? (
          ""
        ) : (
          <AdminBreadCrump
            title={`${"Invoice List"}`}
            companyName={"Loyalstring"}
            module={"Transactions"}
            page={`${"Invoice List"}`}
          />
        )}
      </div>
      {/* <div style={{padding:"5px 20px"}}>
     <div style={{display:'flex',justifyContent:'end'}}>
      
      <Link to={`/invoice_entry_new`} style={{textDecoration:"none",display:'flex',justifyContent:'center',alignItems:'center',gap:"4px",cursor:'pointer',color:"#fff",background:"#0077d4",borderRadius:'5px',padding:"4px 10px"}} >Add <Add/></Link>

    
     </div>
      <ListTable
      columns={columns}
      handleEditData={handleEditData}
      data={InvoiceList?.length ? [...InvoiceList].reverse( ) : []}
      />
      </div> */}

      <div>
        <div
          style={{
            display: "flex",
            gap: "20px",
            display: "flex",
            justifyContent: "end",
            background: "#fff",
            padding: "10px 0px",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Search..."
              style={{
                border: "1px solid gray",
                borderRadius: "0px",
                borderBottomLeftRadius: "8px",
                borderTopLeftRadius: "8px",
                height: "100%",
              }}
              // className={style["serach-input"]}
            />

            <Icon
              style={{
                fontSize: "20px",
                padding: "7px",
                background: "#0077D4",
                color: "#fff",
                height: "20px",
                width: "20px",
                borderBottomRightRadius: "8px",
                borderTopRightRadius: "8px",
              }}
              icon="mdi:magnify"
            />
          </div>

          <Icon
            onClick={() => DownloadExcel(invoiceList)}
            style={{
              fontSize: "28px ",
              color: "#0077d4",
              cursor: "pointer",
            }}
            icon="bx:export"
          />

          <button
          // style={{ width: "30%" }}
          // className={style["custom-btn"]}
          >
            <Icon style={{ fontSize: "24px " }} icon="ic:outline-print" />
            Print
          </button>
          <button
            //  onClick={handlenew}
            onClick={() => navigate("/invoice_entry_new")}
            style={{ margin: 0 }}
            // className={style["custom-btn"]}
          >
            New
            <Icon icon="mdi:plus" width="24" height="24" />
          </button>
        </div>

        <CustomerTable
          columns={invoiceListColumns}
          allCategories={invoiceList?.length ? invoiceList : []}
          handleEditData={(product) =>
            navigate(`/edit_invoice_new/${product?.Id}`)
          }
          handlePrint={(product) => handlePrint(product)}
          searchInput={searchInput}
          setSearchInput={setSearchInput}

          // title={"All Customers"}
        />
      </div>

      <Modal
        isOpen={showFailureModal}
        onRequestClose={() => setShowFailureModal(false)} // Close the modal without any action
        contentLabel="Generation Failed"
        style={{
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -50%)",
          },
        }}
      >
        <h2>Failed to Generate Invoice, IRN, and E-Way Bill</h2>
        <p>
          We were unable to generate the Invoice, IRN, and E-Way Bill. Would you
          like to generate the PDF without these details?
        </p>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <button onClick={() => setShowFailureModal(false)}>Cancel</button>
          <button onClick={handleGenerateWithoutIRN}>Yes, Generate PDF</button>
        </div>
      </Modal>
    </>
  );
};

export default AdminPurchaseEntriesList;
